import React from 'react';
import {
  Datagrid,
  FunctionField,
  ListBase,
  TextField,
  Title,
  useListContext,
} from 'react-admin';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { useTitle } from '@src/hooks/useTitle';
import type { RepairPriceCriteriaRow } from './dataProvider';
import type { RepairPriceCriteriaTag } from './fetchRepairPricing';

const formatPrice = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

const formatTags = (tags: RepairPriceCriteriaTag[]): string => {
  if (!tags || tags.length === 0) return '—';
  return tags.map((t) => `${t.tag}: ${t.name}`).join(', ');
};

const EmptyList = () => {
  const { isPending } = useListContext();
  if (isPending) return null;
  return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography color="text.secondary">No repair pricing configured for this partner.</Typography>
    </Box>
  );
};

const RepairPricingList = () => {
  const { title } = useTitle('Repair Pricing');

  return (
    <>
      <Title title={title} />
      <ListBase perPage={500} disableSyncWithLocation>
        <Datagrid bulkActionButtons={false} empty={<EmptyList />} rowClick="edit">
          <TextField<RepairPriceCriteriaRow> source="problemAttributeName" label="Problem" emptyText="—" />
          <TextField<RepairPriceCriteriaRow> source="locationAttributeName" label="Location" emptyText="—" />
          <TextField<RepairPriceCriteriaRow> source="severityAttributeName" label="Severity" emptyText="—" />
          <TextField<RepairPriceCriteriaRow> source="rubricCode" label="Rubric Code" emptyText="—" />
          <TextField<RepairPriceCriteriaRow> source="vendorCountryCode" label="Vendor Country" emptyText="—" />
          <FunctionField<RepairPriceCriteriaRow>
            label="Category Filters"
            render={(record) => formatTags(record.tags)}
          />
          <FunctionField<RepairPriceCriteriaRow>
            label="Price"
            render={(record) => formatPrice(record.price)}
          />
        </Datagrid>
      </ListBase>
    </>
  );
};

export default RepairPricingList;
